import React from "react";
import { Input, Select } from "../ui/Input";

interface NodeFiltersPanelProps {
  search: string;
  onSearchChange: (search: string) => void;
  scriptId: string;
  onScriptIdChange: (scriptId: string) => void;
  inputType: string;
  onInputTypeChange: (inputType: string) => void;
  globalOnly: boolean;
  onGlobalOnlyChange: (globalOnly: boolean) => void;
  scripts?: Array<{ id: number; name: string }>;
  onClearFilters: () => void;
  hideScriptFilter?: boolean;
  hideGlobalOnly?: boolean;
  messageSearch?: string;
  onMessageSearchChange?: (value: string) => void;
  hideMessageSearch?: boolean;
}

const INPUT_TYPES = [
  { value: "text", label: "Text Input" },
  { value: "email", label: "Email Input" },
  { value: "phone", label: "Phone Input" },
  { value: "number", label: "Number Input" },
  { value: "money", label: "Money Input" },
  { value: "buttons", label: "Buttons" },
  { value: "date-picker", label: "Date Picker" },
];

export default function NodeFiltersPanel({
  search,
  onSearchChange,
  scriptId,
  onScriptIdChange,
  inputType,
  onInputTypeChange,
  globalOnly,
  onGlobalOnlyChange,
  scripts = [],
  onClearFilters,
  hideScriptFilter = false,
  hideGlobalOnly = false,
  messageSearch = "",
  onMessageSearchChange,
  hideMessageSearch = true,
}: NodeFiltersPanelProps) {
  const hasActiveFilters =
    search ||
    (!hideScriptFilter && scriptId) ||
    inputType ||
    (!hideGlobalOnly && globalOnly) ||
    (!!onMessageSearchChange && !hideMessageSearch && messageSearch);

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Search */}
        <div>
          <div className="relative">
            <Input
              label="Search Nodes"
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by node name or template name..."
              variant="default"
              className="pl-10"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Message Search */}
        {!!onMessageSearchChange && !hideMessageSearch && (
          <div>
            <div className="relative">
              <Input
                label="Search Messages"
                type="text"
                value={messageSearch}
                onChange={(e) => onMessageSearchChange(e.target.value)}
                placeholder="Search within message text (all languages)..."
                variant="default"
                className="pl-10"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Script Filter */}
        {!hideScriptFilter && (
          <div>
            <Select
              label="Script"
              value={scriptId}
              onChange={(e) => onScriptIdChange(e.target.value)}
              variant="default"
            >
              <option value="">All Scripts</option>
              <option value="global">Global Nodes Only</option>
              {scripts.map((script) => (
                <option key={script.id} value={script.id.toString()}>
                  {script.name}
                </option>
              ))}
            </Select>
          </div>
        )}

        {/* Input Type Filter */}
        <div>
          <Select
            label="Input Type"
            value={inputType}
            onChange={(e) => onInputTypeChange(e.target.value)}
            variant="default"
          >
            <option value="">All Input Types</option>
            <option value="none">No Input</option>
            {INPUT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Global Only Toggle */}
        {!hideGlobalOnly && (
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={globalOnly}
                onChange={(e) => onGlobalOnlyChange(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">
                Show only global nodes (not tied to any script)
              </span>
            </label>
          </div>
        )}

        {/* Filter Summary */}
        {hasActiveFilters && (
          <div className="pt-3 border-t border-gray-200">
            <div className="text-sm text-gray-600 mb-2">Active filters:</div>
            <div className="flex flex-wrap gap-2">
              {search && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Search: "{search}"
                  <button
                    onClick={() => onSearchChange("")}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {!!onMessageSearchChange &&
                !hideMessageSearch &&
                messageSearch && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                    Message: "{messageSearch}"
                    <button
                      onClick={() => onMessageSearchChange("")}
                      className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-teal-200"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                )}
              {!hideScriptFilter && scriptId && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Script:{" "}
                  {scriptId === "global"
                    ? "Global Nodes"
                    : scripts.find((s) => s.id.toString() === scriptId)?.name ||
                      `ID ${scriptId}`}
                  <button
                    onClick={() => onScriptIdChange("")}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-green-200"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {inputType && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Type:{" "}
                  {inputType === "none"
                    ? "No Input"
                    : INPUT_TYPES.find((t) => t.value === inputType)?.label ||
                      inputType}
                  <button
                    onClick={() => onInputTypeChange("")}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-purple-200"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {!hideGlobalOnly && globalOnly && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Global Only
                  <button
                    onClick={() => onGlobalOnlyChange(false)}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-orange-200"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
